import React, { FC, useEffect, useState } from "react"
import "./takedown-log.css"
import Loading from "../../../../ui/Loading"
import { KSpan, KInput, KButton, KTooltip, KDropdown } from "kahuna-base-react-components"
import { crawlers, users } from "../../../../../apis"
import { DrmSummary, ReleaseTakedownLog, TakedownLog, UserLog } from "../../../../../types"
import { ArrowDown, ArrowUp } from "heroicons-react"
import { formatNumber, mapListToCamelCase } from "../../../../../utility"
//@ts-ignore
import CheckIcon from "../../../../../assets/admin_icons/check-circle.svg"
//@ts-ignore
import CheckRedIcon from "../../../../../assets/admin_icons/check-circle-red.svg"
//@ts-ignore
import CloseIcon from "../../../../../assets/admin_icons/close-circle.svg"
//@ts-ignore
import CloseGreenIcon from "../../../../../assets/admin_icons/close-circle-green.svg"
//@ts-ignore
import AppleMusicIcon from "../../../../../assets/release-platform-icons/Apple-Music.png"
// @ts-ignore
import YoutubeLogo from "../../../../../assets/logo-icons/youtube-logo.png"

import { connect } from "react-redux"
import { RootState } from "../../../../../store"
import { toast } from "react-toastify"
import { exportTakedownLogs } from "./downloadTakedownLogsExcel"
import Pagination from "../../../../ui/table/Pagination"
import { DRM_SUMMARY_ACTION, successFilledToastOptions } from "../../../../../constants"
import { KSelectOption } from "kahuna-base-react-components/dist/components/KDropdown/KDropdown"
import TakedownEmailTemplates from "./TakedownEmailTemplates"
import UpcSelectionModal from "./UpcSelectionModal"

type Record = {
  ALBUM_NAME: string
  TRACK_NAME: string
  UPC: string
  ISRC: string
  URL: string
}

type UserLogMap = {
  [key: number]: UserLog[]
}

const releaseColumnWidths: any = {
  ID: "4%",
  RELEASE: "43%",
  STATUS: "10%",
  SPOTIFY: "11%",
  APPLE: "11%",
  YOUTUBE: "11%",
  WEEKLY_STATS: "10%"
}

const ActionOptions: KSelectOption[] = [
  { value: 0, value2: DRM_SUMMARY_ACTION.IDLE, label: "Idle" },
  { value: 1, value2: DRM_SUMMARY_ACTION.NEW, label: "New" },
  { value: 2, value2: DRM_SUMMARY_ACTION.UNDER_REVIEW, label: "Under Review" },
  { value: 3, value2: DRM_SUMMARY_ACTION.ACTION_TAKEN, label: "Action Taken" },
  { value: 4, value2: DRM_SUMMARY_ACTION.CLOSED, label: "Closed" }
]

type AdminReleaseTakedownLogProps = {
  takedownLog?: TakedownLog
  onRunExisting?: () => void
  onAbortTakedownLog?: (takedownLogId: number) => void
  drmSummary?: DrmSummary
  isDrm?: boolean
}

enum SortOptions {
  ROYALTY_DESC = "-royalty_weekly",
  ROYALTY_ASC = "royalty_weekly",
  STREAM_DESC = "-stream_weekly",
  STREAM_ASC = "stream_weekly"
}

// Pending release selection: the logs the UPCs are picked from, plus the promise waiting on the modal.
type UpcSelection = {
  releaseTakedownLogList: ReleaseTakedownLog[]
  resolve: (upcList: string | null) => void
}

const AdminReleaseTakedownLog: FC<AdminReleaseTakedownLogProps> = (props) => {
  const [loading, setLoading] = useState(false)
  const [releaseTakedownLogList, setReleaseTakedownLogList] = useState<ReleaseTakedownLog[]>([])
  const [filteredReleaseTakedownLogList, setFilteredReleaseTakedownLogList] = useState<ReleaseTakedownLog[]>([])
  const [searchText, setSearchText] = useState("")
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(100)
  const [count, setCount] = useState(0)
  const [openTrackTakedownLogIds, setOpenTrackdownLogIds] = useState<number[]>([])
  const [selectedAction, setSelectedAction] = useState<KSelectOption[]>([
    { value: 0, value2: DRM_SUMMARY_ACTION.IDLE, label: "Idle" },
    { value: 1, value2: DRM_SUMMARY_ACTION.NEW, label: "New" }
  ])
  const [sort, setSort] = useState(SortOptions.ROYALTY_DESC)
  const [upcSelection, setUpcSelection] = useState<UpcSelection>()
  const [openLogsMap, setOpenLogsMap] = useState<UserLogMap>({})

  // The UPC search and the action filter go to the server together, so a combination of the two only
  // lists the releases that match both.
  const buildFilterParams = () => {
    const search = searchText.trim()
    const searchParam = search ? `&search=${encodeURIComponent(search)}` : ""
    const actionParam =
      selectedAction?.length > 0 ? `&action=${selectedAction.map((action) => action.value2).join(",")}` : ""
    return `${searchParam}${actionParam}`
  }

  const fetchReleaseTakedownLogList = () => {
    setLoading(true)
    crawlers(
      `/takedown-logs/release_takedown_logs/?takedown_log_id=${props.takedownLog?.id}${buildFilterParams()}&sort=${sort}`,
      "GET"
    ).then((resp) => {
      setReleaseTakedownLogList(mapListToCamelCase(resp.data.results))
      setFilteredReleaseTakedownLogList(mapListToCamelCase(resp.data.results))
      setLoading(false)
    })
  }

  const fetchDrmTakedownLogList = (offset: number, isExport: boolean, isCopy: boolean) => {
    const limitParam = `&limit=${isExport || isCopy ? 9999999 : limit}`
    setLoading(true)
    crawlers(
      `/takedown-logs/drm_release_takedown_logs/?drm_summary_id=${props.drmSummary?.id}&offset=${offset}${limitParam}${buildFilterParams()}&sort=${sort}`,
      "GET"
    ).then((resp) => {
      setLoading(false)
      if (isExport) {
        onExport(mapListToCamelCase(resp.data.results))
        return
      } else if (isCopy) {
        onCopy(mapListToCamelCase(resp.data.results))
        return
      }
      setReleaseTakedownLogList(mapListToCamelCase(resp.data.results))
      setFilteredReleaseTakedownLogList(mapListToCamelCase(resp.data.results))
      setCount(resp.data.count)
    })
  }

  const openYoutubePlatform = (releaseLog: ReleaseTakedownLog) => {
    if (!releaseLog.release) {
      return
    }
    crawlers(`/takedown-logs/release_isrc_list/?release_id=${releaseLog.release.id}`, "GET").then((resp) => {
      const isrcList: string[] = resp.data.results
      if (isrcList.length === 0) {
        toast.info("No ISRCs found for this release")
        return
      }
      window.open(`https://www.youtube.com/results?search_query=%22${isrcList[0]}%22`, "_blank")
    })
  }

  useEffect(() => {
    if (!props.takedownLog) return
    fetchReleaseTakedownLogList()
  }, [props.takedownLog])

  useEffect(() => {
    if (!props.drmSummary) return
    setOffset(0)
    fetchDrmTakedownLogList(0, false, false)
  }, [props.drmSummary])

  const renderBoolean = (value?: boolean, isReverse?: boolean) => {
    const icon = isReverse ? (value ? CheckRedIcon : CloseGreenIcon) : value ? CheckIcon : CloseIcon
    return <img className="min-w-5 max-w-5 min-h-5 max-h-5" src={icon} alt="status-icon" />
  }

  const openMultiPlatform = (platformUrl?: string, platformIds?: string) => {
    if (!platformUrl || !platformIds) return
    platformIds.split(",").forEach((id) => {
      window.open(`${platformUrl}${id}`, "_blank")
    })
  }

  const buildUpcList = (releaseTakedownLogList: ReleaseTakedownLog[], copyType: string) => {
    return releaseTakedownLogList
      .map((log) => {
        switch (copyType) {
          case "1":
            return log.spotifyComplete && log.appleComplete && log.youtubeComplete ? log.release?.upc : null
          case "2":
            return log.spotifyComplete ? log.release?.upc : null
          case "3":
            return log.appleComplete ? log.release?.upc : null
          case "4":
            return log.youtubeComplete ? log.release?.upc : null
          case "5":
            return !log.spotifyComplete || !log.appleComplete || !log.youtubeComplete ? log.release?.upc : null
          case "6":
            return !log.spotifyComplete ? log.release?.upc : null
          case "7":
            return !log.appleComplete ? log.release?.upc : null
          case "8":
            return !log.youtubeComplete ? log.release?.upc : null
          default:
            return null
        }
      })
      .filter((upc) => upc !== null)
      .join(",")
  }

  // Opens the release selection modal and resolves with the matching UPCs comma separated,
  // or with null when the modal is dismissed.
  const selectUpcList = (releaseTakedownLogList: ReleaseTakedownLog[]): Promise<string | null> =>
    new Promise((resolve) => setUpcSelection({ releaseTakedownLogList, resolve }))

  const onUpcOptionSelect = (copyType: string) => {
    if (!upcSelection) return

    const { releaseTakedownLogList, resolve } = upcSelection
    setUpcSelection(undefined)
    resolve(buildUpcList(releaseTakedownLogList, copyType))
  }

  const onUpcSelectionClose = () => {
    if (!upcSelection) return

    upcSelection.resolve(null)
    setUpcSelection(undefined)
  }

  const onCopy = async (filteredReleaseTakedownLogList: ReleaseTakedownLog[]) => {
    const upcList = await selectUpcList(filteredReleaseTakedownLogList)
    if (upcList === null) {
      return
    }

    if (!upcList) {
      toast.info("No UPCs found for the selected option.")
      return
    }

    navigator.clipboard.writeText(upcList)
    toast.success("Copied to clipboard.")
  }

  const onExport = (filteredReleaseTakedownLogList: ReleaseTakedownLog[]) => {
    if (filteredReleaseTakedownLogList.length === 0) {
      toast.info("No records to export.")
      return
    }
    const filenameSuffix = props.takedownLog?.id ? `_${props.takedownLog.id}` : ""
    exportTakedownLogs(filteredReleaseTakedownLogList, `takedown_logs${filenameSuffix}.xlsx`)
  }

  const onRunExisting = () => {
    if (!props.takedownLog || !props.onRunExisting) {
      return
    }

    if (
      !window.confirm("This will run the takedown process again for releases with existing Spotify IDs. Are you sure?")
    ) {
      return
    }
    setLoading(true)
    crawlers(`/takedown-logs/run_existing_takedown_log_check/`, "POST", {
      user_id: props.takedownLog.userId,
      takedown_finished: true,
      existing_takedown_log_id: props.takedownLog.id
    })
      .then((resp) => {
        setLoading(false)
        if (!resp.data.success) {
          toast.error(`Error: ${resp.data.message || "Failed to start takedown process."}`)
          return
        }
        toast.success("Takedown process started for releases with existing Spotify IDs.")
        if (props.onRunExisting) {
          props.onRunExisting()
        }
      })
      .catch(() => {
        toast.error("Failed to start takedown process.")
        setLoading(false)
      })
  }

  const onApplyFilters = () => {
    setOffset(0)
    if (props.drmSummary) {
      fetchDrmTakedownLogList(0, false, false)
    } else {
      fetchReleaseTakedownLogList()
    }
  }

  const renderDrmSummaryColor = (releaseLog: ReleaseTakedownLog) => {
    if (!props.isDrm) return "transparent"

    switch (releaseLog.action) {
      case DRM_SUMMARY_ACTION.NEW:
        return "#f5c6cb"
      case DRM_SUMMARY_ACTION.UNDER_REVIEW:
        return "#ffeeba"
      case DRM_SUMMARY_ACTION.ACTION_TAKEN:
        return "#c3e6cb"
      case DRM_SUMMARY_ACTION.CLOSED:
        return "#c3e6cb"
      default:
        return "#f5c6cb"
    }
  }

  const onAbortTakedownLog = () => {
    if (!props.takedownLog || !props.onAbortTakedownLog) {
      return
    }

    if (!window.confirm("Are you sure you want to abort this takedown log?")) {
      return
    }

    props.onAbortTakedownLog(props.takedownLog.id)
  }

  const onClickPagination = (pageNumber: number) => {
    setOffset((pageNumber - 1) * limit)
    fetchDrmTakedownLogList((pageNumber - 1) * limit, false, false)
  }

  const onActionClick = (releaseLog: ReleaseTakedownLog, action?: string) => {
    const log = window.prompt("Enter log message for this action (optional):")

    if (log === null) {
      return
    }

    setLoading(true)
    crawlers(`/takedown-logs/update_release_takedown_action/`, "PUT", {
      id: releaseLog.id,
      action: action || "",
      log
    }).then((resp) => {
      setLoading(false)
      if (!resp.data.success) {
        toast.error(`Error: ${resp.data.message || "Failed to update action."}`)
        return
      }
      setReleaseTakedownLogList(
        releaseTakedownLogList.map((rtl) => {
          if (rtl.id === releaseLog.id) rtl.action = action
          return rtl
        })
      )

      setFilteredReleaseTakedownLogList(
        filteredReleaseTakedownLogList.map((frtl) => {
          if (frtl.id === releaseLog.id) frtl.action = action
          return frtl
        })
      )
      toast.success(`Success!`)
    })
  }

  const renderActions = (releaseLog: ReleaseTakedownLog) => {
    return (
      <KTooltip
        backgroundColor="black"
        position="bottom"
        content={
          <div className="flex flex-col gap-1">
            <KSpan color="white" text={releaseLog.log} />
          </div>
        }
        children={
          <div className="flex gap-2 mt-1">
            <KButton
              width="50px"
              height="40px"
              text="Idle"
              onClick={() => onActionClick(releaseLog, undefined)}
              background={!releaseLog.action ? "#000000da" : "#00000011"}
              textColor={!releaseLog.action ? "white" : "#666666"}
            />
            <KButton
              width="50px"
              height="40px"
              text="New"
              onClick={() => onActionClick(releaseLog, DRM_SUMMARY_ACTION.NEW)}
              background={releaseLog.action === DRM_SUMMARY_ACTION.NEW ? "#000000da" : "#00000011"}
              textColor={releaseLog.action === DRM_SUMMARY_ACTION.NEW ? "white" : "#666666"}
            />
            <KButton
              width="65px"
              height="40px"
              text="Under Review"
              onClick={() => onActionClick(releaseLog, DRM_SUMMARY_ACTION.UNDER_REVIEW)}
              background={releaseLog.action === DRM_SUMMARY_ACTION.UNDER_REVIEW ? "#000000da" : "#00000011"}
              textColor={releaseLog.action === DRM_SUMMARY_ACTION.UNDER_REVIEW ? "white" : "#666666"}
            />
            <KButton
              width="60px"
              height="40px"
              text="Action Taken"
              onClick={() => onActionClick(releaseLog, DRM_SUMMARY_ACTION.ACTION_TAKEN)}
              background={releaseLog.action === DRM_SUMMARY_ACTION.ACTION_TAKEN ? "#000000da" : "#00000011"}
              textColor={releaseLog.action === DRM_SUMMARY_ACTION.ACTION_TAKEN ? "white" : "#666666"}
            />
            <KButton
              width="60px"
              height="40px"
              text="Closed"
              onClick={() => onActionClick(releaseLog, DRM_SUMMARY_ACTION.CLOSED)}
              background={releaseLog.action === DRM_SUMMARY_ACTION.CLOSED ? "#000000da" : "#00000011"}
              textColor={releaseLog.action === DRM_SUMMARY_ACTION.CLOSED ? "white" : "#666666"}
            />
          </div>
        }
      />
    )
  }

  const onClaimReleaseUser = (releaseLogId?: number, platform?: string) => {
    setLoading(true)
    crawlers(`/takedown-logs/create_release_user_claim/`, "POST", {
      platform,
      release_takedown_log_id: releaseLogId
    })
      .then((resp) => {
        setLoading(false)
        if (!resp.data.success) {
          toast.error(`Failed to claim`)
        }
        filteredReleaseTakedownLogList.forEach((releaseLog) => {
          if (releaseLog.id === releaseLogId) {
            if (platform === "spotify") {
              releaseLog.spotifyClaimed = true
            } else if (platform === "apple") {
              releaseLog.appleClaimed = true
            }
          }
        })
        if (platform === "spotify") {
          window.open("https://support.spotify.com/tr-tr/reports/legal/", "_blank")
        } else if (platform === "apple") {
          window.open(
            "https://www.apple.com/legal/intellectual-property/dispute-forms/music/music-dispute.html",
            "_blank"
          )
        }
        setFilteredReleaseTakedownLogList([...filteredReleaseTakedownLogList])
      })
      .catch(() => {
        toast.error(`Failed to claim`)
        setLoading(false)
      })
  }

  const onClickOpenTrackTakedownLogs = (releaseLogId?: number) => {
    if (!releaseLogId) {
      return
    }
    if (openTrackTakedownLogIds.includes(releaseLogId)) {
      setOpenTrackdownLogIds((prev) => prev.filter((id) => id !== releaseLogId))
    } else {
      setOpenTrackdownLogIds((prev) => [...prev, releaseLogId])
    }
  }

  const onClickLog = async (releaseLog?: ReleaseTakedownLog) => {
    if (!releaseLog) {
      return
    }

    if (releaseLog.id in openLogsMap) {
      delete openLogsMap[releaseLog.id]
    } else {
      const resp = await users(`/user_log/?type=drm_log_action&model=Release&entity_id=${releaseLog.releaseId}`, "GET")

      const userLogList = mapListToCamelCase(resp.data.results)
      openLogsMap[releaseLog.id] = userLogList
    }

    setOpenLogsMap({ ...openLogsMap })
  }

  const renderPlatformBlock = (
    platformUrl?: string,
    platformIds?: string,
    visible?: boolean,
    complete?: boolean,
    linkCount?: number,
    releaseLog?: ReleaseTakedownLog,
    platform?: string,
    extra?: React.ReactNode
  ) => {
    const claimButton = () => {
      if (platform === "youtube") {
        return
      }
      if (platform === "spotify" && (releaseLog?.spotifyLinkCount || 0) < 2) {
        return
      }
      if (platform === "apple" && (releaseLog?.appleLinkCount || 0) < 2) {
        return
      }

      let claimText = "Claim"
      let background = "black"
      let textColor = "white"
      if (platform === "spotify" && releaseLog?.spotifyClaimed) {
        claimText = "Claimed"
        background = "#00000011"
        textColor = "#666666"
      } else if (platform === "apple" && releaseLog?.appleClaimed) {
        claimText = "Claimed"
        background = "#00000011"
        textColor = "#666666"
      }

      return (
        <KButton
          background={background}
          textColor={textColor}
          text={claimText}
          height="28px"
          width="68px"
          onClick={() => onClaimReleaseUser(releaseLog?.id, platform)}
        />
      )
    }

    const renderTrackTakedownLogs = () => {
      if (platform !== "youtube") {
        return
      }
      return (
        <div className="flex cursor-pointer" onClick={() => onClickOpenTrackTakedownLogs(releaseLog?.id)}>
          <KSpan text="#Tracks: " />
          <img src="/chevron-down.svg" />
          <KSpan text={releaseLog?.trackTakedownLogs?.length.toString() || "0"} />
        </div>
      )
    }

    if (props.isDrm) {
      return (
        <div>
          <div className="flex gap-10">
            <KSpan text="Links:" />
            <div
              className={platformUrl ? "cursor-pointer" : ""}
              onClick={() => openMultiPlatform(platformUrl, platformIds)}
            >
              <KSpan text={linkCount?.toString() || "0"} />
            </div>
          </div>
          {claimButton()}
          {renderTrackTakedownLogs()}
        </div>
      )
    }

    return (
      <div className="flex flex-col gap-1">
        <div className="flex gap-10">
          <KSpan text="Links:" />
          <div
            className={platformUrl ? "cursor-pointer" : ""}
            onClick={() => openMultiPlatform(platformUrl, platformIds)}
          >
            <KSpan text={linkCount?.toString() || "0"} />
          </div>
        </div>
        <div className="flex gap-6">
          <KSpan text="Visible:" />
          {renderBoolean(visible, false)}
        </div>
        <div className="flex gap-1">
          <KSpan text="Complete:" />
          {renderBoolean(complete, false)}
        </div>
        {extra}
        {claimButton()}
        {renderTrackTakedownLogs()}
      </div>
    )
  }

  const renderArrow = () => {
    return sort.includes("-") ? (
      <ArrowDown className={"inline h-[10px] mb-[2px]"} style={{ color: "#737373" }} />
    ) : (
      <ArrowUp className={"inline h-[10px] mb-[2px]"} style={{ color: "#737373" }} />
    )
  }

  const claimYoutube = () => {
    const reqUrl = props.drmSummary
      ? `/takedown-logs/process_drm_youtube_claim/?drm_summary_id=${props.drmSummary?.id}`
      : `/takedown-logs/process_takedown_log_youtube_claim/?takedown_log_id=${props.takedownLog?.id}`

    setLoading(true)
    crawlers(`${reqUrl}`, "PUT")
      .then((resp) => {
        setLoading(false)
        if (!resp.data) {
          toast.error("Failed to claim YouTube")
          return
        }

        toast.info("YouTube claimed successfully. Preparing Email.")
        const records: Record[] = resp.data.records
        const subject = "Distribution Dispute Request & YouTube Link Verification"
        const intro = resp.data.email_content

        const tableHtml = `
          <table border="1" cellpadding="6" cellspacing="0" style="border-collapse: collapse; font-size: 13px;">
            <thead>
              <tr>
                <th>Album Name</th>
                <th>Track Name</th>
                <th>UPC</th>
                <th>ISRC</th>
                <th>URL</th>
              </tr>
            </thead>
            <tbody>
              ${records
                .map(
                  (r) => `
                  <tr>
                    <td>${r.ALBUM_NAME}</td>
                    <td>${r.TRACK_NAME}</td>
                    <td>${r.UPC}</td>
                    <td>${r.ISRC}</td>
                    <td><a href="${r.URL}">${r.URL}</a></td>
                  </tr>
                `
                )
                .join("")}
            </tbody>
          </table>
        `

        const body = `${intro}\n\n\n`

        navigator.clipboard.write([
          new ClipboardItem({
            "text/html": new Blob([tableHtml], { type: "text/html" })
          })
        ])

        const mailtoLink = `mailto:copyright@youtube.com?subject=${encodeURIComponent(
          subject
        )}&body=${encodeURIComponent(body)}`

        window.location.href = mailtoLink
        toast.success(`Copied html table to clipboard. You can paste it into email.`, {
          ...successFilledToastOptions,
          autoClose: 60000
        })
      })
      .catch((err) => {
        setLoading(false)
        toast.error("Failed to claim YouTube")
      })
  }

  const renderYoutubeClaimButton = () => {
    let text = "Claim YouTube"
    let background = "black"
    let textColor = "white"

    if (props.drmSummary && props.drmSummary.youtubeClaimed) {
      text = "YouTube Claimed"
      background = "#00000011"
      textColor = "#666666"
    } else if (props.takedownLog && props.takedownLog.youtubeClaimed) {
      text = "YouTube Claimed"
      background = "#00000011"
      textColor = "#666666"
    }

    return (
      <KButton width="100px" text={text} onClick={() => claimYoutube()} background={background} textColor={textColor} />
    )
  }

  const onSendInfringementEmail = (releaseLogId?: number) => {
    const answer = window.prompt(
      "Are you sure you want to send infringement email to user?\n\nBy default email will be sent in ENGLISH.\n\nType 'tr' to send turkish."
    )

    if (answer === null) {
      return
    }

    setLoading(true)
    crawlers(`/takedown-logs/send_infringement_email/`, "POST", {
      release_takedown_log_id: releaseLogId,
      lang: answer
    })
      .then((resp) => {
        setLoading(false)
        if (!resp.data.success) {
          toast.error(`Failed to send infringement email`)
        }
        filteredReleaseTakedownLogList.forEach((releaseLog) => {
          if (releaseLog.id === releaseLogId) {
            releaseLog.infringementSent = true
          }
        })

        setFilteredReleaseTakedownLogList([...filteredReleaseTakedownLogList])
        toast.success("Successfully sent email")
      })
      .catch(() => {
        toast.error(`Failed to send infringement email`)
        setLoading(false)
      })
  }

  const renderInfrigementEmailButton = (releaseLog: ReleaseTakedownLog) => {
    if (
      (releaseLog?.appleLinkCount || 0) < 2 &&
      (releaseLog?.spotifyLinkCount || 0) < 2 &&
      (releaseLog?.youtubeLinkCount || 0) < 2
    ) {
      return <div />
    }

    let text = "Infrigement Send Email"
    let background = "black"
    let textColor = "white"
    let disabled = false

    if (releaseLog.infringementSent) {
      text = "Infringement Sent"
      background = "#00000011"
      textColor = "#666666"
      disabled = true
    }
    return (
      <KButton
        width="86px"
        text={text}
        disabled={disabled}
        onClick={() => onSendInfringementEmail(releaseLog.id)}
        background={background}
        textColor={textColor}
      />
    )
  }

  const renderReleaseTakedownLogs = () => {
    return (
      <div className="pt-4 pb-4 flex flex-col">
        {props.takedownLog && !props.isDrm && (
          <div className="mb-4">
            <TakedownEmailTemplates
              takedownLog={props.takedownLog}
              getUpcList={() => selectUpcList(filteredReleaseTakedownLogList)}
            />
          </div>
        )}
        <div className="mb-6 flex justify-between">
          <div className="flex gap-2">
            <KButton
              width="90px"
              text="Copy UPC List"
              onClick={() =>
                props.drmSummary ? fetchDrmTakedownLogList(0, false, true) : onCopy(filteredReleaseTakedownLogList)
              }
              background="black"
              textColor="white"
            />
            <KButton
              width="70px"
              text="Export"
              onClick={() =>
                props.drmSummary ? fetchDrmTakedownLogList(0, true, false) : onExport(filteredReleaseTakedownLogList)
              }
              background="black"
              textColor="white"
            />
            {renderYoutubeClaimButton()}
          </div>
          {props.onRunExisting && props.takedownLog?.status !== "in_progress" && (
            <div>
              <KButton
                width="160px"
                text="Auto Run with Existing Spotify ID"
                onClick={() => onRunExisting()}
                background="white"
              />
            </div>
          )}
          {props.onAbortTakedownLog && props.takedownLog?.status === "in_progress" && (
            <div>
              <KButton
                width="280px"
                text="Abort Takedown Log"
                onClick={() => onAbortTakedownLog()}
                background="white"
              />
            </div>
          )}

          <div className="flex gap-4">
            {props.isDrm && (
              <KDropdown
                placeholder="Select Action"
                width={180}
                isMulti
                closeMenuOnSelect={false}
                menuWidth={170}
                isClearable
                options={ActionOptions}
                selected={selectedAction}
                onSelect={(selected) => setSelectedAction(selected as KSelectOption[])}
              />
            )}
            <KInput
              width={120}
              onChange={(text) => setSearchText(text)}
              onKeyDown={(event) => event.key === "Enter" && onApplyFilters()}
              value={searchText}
              placeholder="Search UPC"
            />
            <KButton background="white" width="80px" text="Apply Filters" onClick={onApplyFilters} />
          </div>
        </div>
        {/* Header */}
        <div className="flex pb-2 gap-2">
          {Object.keys(releaseColumnWidths).map((header) => (
            <div key={`release_header_${header}`} style={{ width: releaseColumnWidths[header] }}>
              {header === "WEEKLY_STATS" ? (
                <div className="items-center">
                  <div
                    className="flex gap-1 items-center cursor-pointer"
                    onClick={() =>
                      setSort(sort !== SortOptions.ROYALTY_DESC ? SortOptions.ROYALTY_DESC : SortOptions.ROYALTY_ASC)
                    }
                  >
                    <KSpan fontWeight={600} text={"ROYALTY"} />
                    {(sort === SortOptions.ROYALTY_DESC || sort === SortOptions.ROYALTY_ASC) && renderArrow()}
                  </div>
                  <div
                    className="flex gap-1 items-center cursor-pointer"
                    onClick={() =>
                      setSort(sort !== SortOptions.STREAM_DESC ? SortOptions.STREAM_DESC : SortOptions.STREAM_ASC)
                    }
                  >
                    <KSpan fontWeight={600} text={"STREAM"} />
                    {(sort === SortOptions.STREAM_DESC || sort === SortOptions.STREAM_ASC) && renderArrow()}
                  </div>
                </div>
              ) : (
                <KSpan fontWeight={600} text={header} />
              )}
            </div>
          ))}
        </div>

        {/* Rows */}
        {filteredReleaseTakedownLogList.map((releaseLog) => (
          <div
            key={`release_log_${releaseLog.id}`}
            className="pt-2"
            style={{
              borderTop: "1px dashed grey",
              backgroundColor: renderDrmSummaryColor(releaseLog)
            }}
          >
            <div className="flex gap-2 mb-2">
              {/* ID */}
              <div style={{ width: releaseColumnWidths.ID }}>
                <KSpan text={releaseLog.id?.toString() || "-"} />
              </div>

              {/* RELEASE */}
              <div style={{ width: releaseColumnWidths.RELEASE }}>
                <div className="flex gap-4">
                  <div>
                    <img src={releaseLog.release?.coverFileUrl} width={95} height={95} style={{ borderRadius: 5 }} />
                  </div>
                  <div className="flex flex-col gap-1">
                    <div className="flex gap-1">
                      <KSpan text={`${releaseLog.release?.title || "-"}`} fontWeight={500} />
                      {releaseLog.release?.version && <KSpan text={`(${releaseLog.release?.version || ""})`} />}
                    </div>
                    <div className="flex gap-2 mt-1">
                      <KSpan text={`UPC: ${releaseLog.release?.upc || "-"}`} />
                      {releaseLog.spotifyId ? (
                        <img
                          className="cursor-pointer"
                          onClick={() => {
                            if (!releaseLog.spotifyId) return
                            window.open(`https://open.spotify.com/album/${releaseLog.spotifyId}`, "_blank")
                          }}
                          src={"/account_icons/social_media_icons/spotify.svg"}
                          alt={`${releaseLog.id}_spotify_link`}
                          style={{ width: 20, height: 20, minWidth: 20, minHeight: 20, cursor: "pointer" }}
                        />
                      ) : (
                        <KSpan text="-" />
                      )}

                      {releaseLog.appleId ? (
                        <img
                          onClick={() => {
                            if (!releaseLog.appleId) return
                            window.open(`https://music.apple.com/us/album/${releaseLog.appleId}`, "_blank")
                          }}
                          src={AppleMusicIcon}
                          alt={`${releaseLog.id}_spotify_link`}
                          style={{ width: 20, height: 20, minWidth: 20, minHeight: 20, cursor: "pointer" }}
                        />
                      ) : (
                        <KSpan text="-" />
                      )}

                      <img
                        onClick={() => openYoutubePlatform(releaseLog)}
                        src={YoutubeLogo}
                        alt={`${releaseLog.id}_youtube_link`}
                        style={{ width: 20, height: 20, minWidth: 20, minHeight: 20, cursor: "pointer" }}
                      />
                    </div>
                    {props.isDrm && renderActions(releaseLog)}
                  </div>
                </div>
              </div>

              {/* STATUS */}
              <div style={{ width: releaseColumnWidths.STATUS }}>
                <KSpan text={releaseLog.status || "-"} />
                {props.isDrm && renderInfrigementEmailButton(releaseLog)}
              </div>

              {/* SPOTIFY */}
              <div style={{ width: releaseColumnWidths.SPOTIFY }}>
                {renderPlatformBlock(
                  "https://open.spotify.com/album/",
                  releaseLog.linkedSpotifyIds,
                  releaseLog.spotifyVisible,
                  releaseLog.spotifyComplete,
                  releaseLog.spotifyLinkCount,
                  releaseLog,
                  "spotify",
                  <div className="flex " style={{ gap: 39 }}>
                    <KSpan text="Split:" />
                    {renderBoolean(releaseLog.spotifySplitExist, true)}
                  </div>
                )}
              </div>

              {/* APPLE */}
              <div style={{ width: releaseColumnWidths.APPLE }}>
                {renderPlatformBlock(
                  `https://music.apple.com/us/album/`,
                  releaseLog.linkedAppleIds,
                  releaseLog.appleVisible,
                  releaseLog.appleComplete,
                  releaseLog.appleLinkCount,
                  releaseLog,
                  "apple"
                )}
              </div>

              {/* YOUTUBE */}
              <div style={{ width: releaseColumnWidths.YOUTUBE }}>
                {renderPlatformBlock(
                  undefined,
                  undefined,
                  releaseLog.youtubeVisible,
                  releaseLog.youtubeComplete,
                  releaseLog.youtubeLinkCount,
                  releaseLog,
                  "youtube"
                )}
              </div>

              {/* ROYALTY AND STREAM WEEKLY */}
              <div style={{ width: releaseColumnWidths.WEEKLY_STATS }}>
                <div>
                  <KSpan text={`$${formatNumber(releaseLog.royaltyWeekly || 0, 2)}`} />
                </div>
                <div>
                  <KSpan text={`${formatNumber(releaseLog.streamWeekly || 0, 1)}`} />
                </div>
                {props.isDrm && (
                  <KButton
                    background="transparent"
                    shadowDisabled
                    text="Logs"
                    height="28px"
                    onClick={() => onClickLog(releaseLog)}
                  />
                )}
              </div>
            </div>
            {openTrackTakedownLogIds.includes(releaseLog.id) && (
              <div
                className="flex flex-row flex-nowrap gap-4 mb-2 overflow-x-auto thin-scrollbar pb-2 ml-2"
                style={{ width: "140vh" }}
              >
                {releaseLog.trackTakedownLogs?.map((trackLog) => {
                  return (
                    <div key={`track_log_${trackLog.id}`} className="flex gap-2 flex-shrink-0 mt-1">
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-1">
                          <KSpan text={`ISRC: ${trackLog.isrc}`} fontWeight={500} />
                        </div>
                        <div className="flex gap-10">
                          <KSpan text="Links:" />
                          <div
                            className={"cursor-pointer"}
                            onClick={() =>
                              window.open(
                                `https://www.youtube.com/results?search_query=%22${trackLog.isrc}%22`,
                                "_blank"
                              )
                            }
                          >
                            <KSpan text={trackLog.youtubeLinkCount?.toString() || "0"} />
                          </div>
                        </div>
                        <div className="flex gap-6">
                          <KSpan text="Visible:" />
                          {renderBoolean(trackLog.youtubeVisible, false)}
                        </div>
                        <div className="flex gap-1">
                          <KSpan text="Complete:" />
                          {renderBoolean(trackLog.youtubeComplete, false)}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
            {releaseLog.id in openLogsMap && (
              <div className="flex flex-col  gap-4 mb-2 overflow-x-auto thin-scrollbar pb-2" style={{ width: "140vh" }}>
                {openLogsMap[releaseLog.id].length > 0 ? (
                  openLogsMap[releaseLog.id].map((userLog: UserLog) => {
                    return (
                      <div key={`track_log_${userLog.id}`} className="flex gap-2 flex-shrink-0 mt-1 ml-2">
                        <div className="flex flex-col">
                          <div className="flex flex-row gap-1">
                            <KSpan text={`${userLog.user?.firstName} ${userLog.user?.lastName}:`} fontWeight={500} />
                            <KSpan text={`${userLog.log || "-"}`} fontWeight={500} />
                          </div>
                          <div className="flex gap-10">
                            <KSpan
                              fontSize={11}
                              text={`Created: ${userLog.created?.substring(0, 19)}`}
                              fontWeight={500}
                            />
                            <KSpan fontSize={11} text={`Status: ${userLog.status}`} fontWeight={500} />
                          </div>
                        </div>
                      </div>
                    )
                  })
                ) : (
                  <div className="ml-2">
                    <KSpan text="No Logs Available" />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="catalog flex flex-col bg-[#fff]">
      {loading && <Loading />}
      <UpcSelectionModal open={!!upcSelection} onSelect={onUpcOptionSelect} onClose={onUpcSelectionClose} />
      {renderReleaseTakedownLogs()}
      {props.drmSummary && (
        <div>
          <Pagination
            key={""}
            hidden={false}
            size={"sm"}
            pageCount={Math.ceil(count / limit)}
            currentPage={offset / limit + 1}
            onClickPage={onClickPagination}
          />
        </div>
      )}
    </div>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    user: state.auth.user
  }
}

export default connect(mapStateToProps, {})(AdminReleaseTakedownLog)
